import { type FormEvent, useState } from "react";
import { getUsersApi } from "@praesto/core";
import { Modal, Button, FormField } from "@praesto/ui-kit";
import type { Translate } from "../lib/time";
import { uploadIntegrationExport, UploadExportError } from "./integrations-client";

/**
 * Local copy of `IntegrationsSettings.tsx`'s `withApi` helper (same
 * `getUsersApi().getAuthToken()` shape). Not imported from there: this
 * modal is itself imported BY `IntegrationsSettings.tsx`, so importing
 * `withApi` back from it would create a circular module dependency.
 */
async function withApi<T>(fn: (apiBase: string, idToken: string) => Promise<T>): Promise<T> {
  const api = getUsersApi();
  const idToken = await api.getAuthToken();
  return fn(api.apiBase, idToken);
}

/** Recognise a Lista in whatever the user picked — filename routing is what the
 *  file-source itself does (`"lista dipendenti"` / `.zip` carries one). */
function looksLikeLista(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes("lista") || n.endsWith(".zip");
}
function looksLikePresenze(name: string): boolean {
  return name.toLowerCase().includes("presenze");
}

/**
 * UploadExportModal — owner upload of Dipendenti in Cloud exports for a file-mode
 * HR connector (`ingestion === "file"`).
 *
 * Takes MULTIPLE files, deliberately. A DiC import is not one file: the Generale
 * ZIP carries the Lista dipendenti and the payslip PDFs — NOT Presenze or Bilanci,
 * which are separate downloads. Uploading the Generale alone imports the staff list
 * and zero absences, which is the entire point of the connector.
 *
 * Order matters too. The Lista is what builds the `name → email` index the other
 * sheets resolve against; a Presenze that lands in a sync with no Lista alongside it
 * (and no employeeMap yet, i.e. a first import) has its absences skipped — and the
 * cursor advances past the file regardless, so they are lost. Uploading everything
 * in one go is therefore not a convenience, it is the correct usage, and the modal
 * says so rather than leaving the owner to find out from an empty leave list.
 */
export function UploadExportModal(props: {
  readonly integrationId: string;
  readonly onUploaded: (result: { uploaded: number }) => void;
  readonly onClose: () => void;
  readonly t: Translate;
}) {
  const { integrationId, onUploaded, onClose, t } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasLista = files.some((f) => looksLikeLista(f.name));
  const hasPresenze = files.some((f) => looksLikePresenze(f.name));
  // Not a blocker — the owner may legitimately be topping up a later month — but
  // the case that silently drops absences deserves to be named before they submit.
  const warnNoLista = hasPresenze && !hasLista;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      // Sequential, not parallel: the files land under increasing Storage
      // generations, and the sync reads them in that order.
      for (const file of files) {
        await withApi((apiBase, idToken) =>
          uploadIntegrationExport(apiBase, integrationId, file, idToken),
        );
      }
      onUploaded({ uploaded: files.length });
      onClose();
    } catch (e) {
      let errorMsg = t("settings.integrations.upload.error");
      if (e instanceof UploadExportError) {
        if (e.code === "file_too_large" && e.maxBytes !== undefined) {
          const maxMb = Math.ceil(e.maxBytes / (1024 * 1024));
          errorMsg = t("settings.integrations.upload.errorTooLarge", { maxMb });
        } else if (e.code === "mime_not_allowed") {
          errorMsg = t("settings.integrations.upload.errorMime");
        } else if (e.code === "storage_unavailable") {
          errorMsg = t("settings.integrations.upload.errorStorage");
        }
      }
      setError(errorMsg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={t("settings.integrations.upload.title")}>
      <form onSubmit={submit} className="space-y-3">
        <FormField label={t("settings.integrations.upload.fileLabel")} required>
          <input
            type="file"
            accept=".xlsx,.zip"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            required
            className="text-sm"
          />
        </FormField>

        <p className="text-xs text-fg-muted">{t("settings.integrations.upload.hint")}</p>

        {files.length > 0 && (
          <ul className="text-caption text-fg-muted space-y-0.5">
            {files.map((f) => (
              // name alone collides when the same basename is picked from two
              // directories (e.g. gen-2026/presenze.xlsx + febb-2026/presenze.xlsx);
              // size disambiguates without needing the (unavailable) full path.
              <li key={`${f.name}_${f.size}`}>· {f.name}</li>
            ))}
          </ul>
        )}

        {warnNoLista && (
          <p className="text-sm text-warning">{t("settings.integrations.upload.warnNoLista")}</p>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" className="w-full sm:w-auto" onClick={onClose}>
            {t("settings.integrations.upload.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="w-full sm:w-auto"
            loading={busy}
            disabled={busy || files.length === 0}
          >
            {busy
              ? t("settings.integrations.upload.busy")
              : t("settings.integrations.upload.submit")}
          </Button>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}
